import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { categoryDetailAPI, userAPI } from '../../services/api';
import Filter from '../../components/ui/Filter';

const TransactionFilters = ({
  filters,
  onFiltersChange,
  onExport,
  onReset,
  userRole = 'MANAGER', // Default to MANAGER for admin view
  user, // Add user prop to access caisse information
  hasActiveFilters = false,
  hasSelection = false,
  transactions,
  onDatePickerOpenChange
}) => {
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true);
      setError(null);
      try {
               // Use different API calls based on user role
               if (userRole === 'AGENT') {
                 const caisseId = user?.assignedCaisses?.[0]?.id;

                 if (!caisseId) {
                   setLoading(false);
                   return;
                 }

                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryDetailAPI.getForFiltersByCaisse(caisseId),
                   userAPI.getForFiltersByCaisseTransactions([caisseId])
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Users are already filtered by the backend (desk agents + managers who created transactions)
                 setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
                 } else if (userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') {
                 // For pointage agents, get pointage categories for their assigned caisses
                 const assignedCaisseIds = user?.assignedCaisses?.map(caisse => caisse.id) || [];

                 if (assignedCaisseIds.length === 0) {
                   setLoading(false);
                   return;
                 }

                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryDetailAPI.getForFilters({ 
                     pointageOnly: true, 
                     caisseIds: assignedCaisseIds 
                   }),
                   userAPI.getForFiltersByCaisseTransactions(assignedCaisseIds)
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Users are already filtered by the backend
                 setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
               } else {
                 // For managers, get all category details
                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryDetailAPI.getForFilters(),
                   userAPI.getForFilters()
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Filter users to show desk agents and managers (both can create transactions)
                 const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
                 const relevantUsers = allUsers.filter(user => user.role === 'AGENT' || user.role === 'MANAGER');
                 setUsers(relevantUsers);
               }
      } catch (error) {
        console.error('Error loading filter options:', error);
        setError('Erreur lors du chargement des filtres');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, [userRole, user]);

  const handleFilterChange = (filterName, value) => {
    let updatedFilters = {
      ...filters,
      [filterName]: value
    };

    if (filterName === 'categoryDetailIds') {
      // Flatten when option values are arrays (category grouped by label)
      const raw = value || [];
      updatedFilters.categoryDetailIds = raw.some(Array.isArray) ? raw.flat(Infinity) : raw;
    }

    onFiltersChange(updatedFilters);
  };

  const getCategoryOptions = () => {
    const selectedCaisseIds = filters.caisseIds || [];
    const inScope = selectedCaisseIds.length === 0
      ? categories
      : categories.filter(cd => selectedCaisseIds.includes(cd.caisse?.id));

    const byLabel = new Map();
    inScope.forEach(categoryDetail => {
      const label = categoryDetail.category?.label || categoryDetail.label;
      if (!byLabel.has(label)) byLabel.set(label, []);
      byLabel.get(label).push(categoryDetail.id);
    });
    return Array.from(byLabel.entries()).map(([label, ids]) => ({
      id: ids[0],
      value: ids,
      label
    }));
  };

        // Define filter configuration
        const baseFilters = [
          {
            name: 'searchTerm',
            type: 'search',
            label: 'Rechercher',
            placeholder: 'Recherche...',
            isMultiSelect: false
          },
          {
            name: 'categoryDetailIds',
            type: 'dropdown',
            label: 'Catégorie',
            placeholder: 'Catégorie: Toutes',
            isMultiSelect: true,
            options: getCategoryOptions()
          }
        ];

        // Add remaining filters
        baseFilters.push(
          {
            name: 'userIds',
            type: 'dropdown',
            label: 'Utilisateur',
            placeholder: 'Utilisateur: Tous',
            isMultiSelect: true,
            options: Array.isArray(users) ? users.map(user => ({
              id: user.id,
              value: user.id,
              label: `${user.firstName} ${user.lastName}`
            })) : []
          }
        );

        // Add status pointage filter for desk agents
        if (userRole === 'AGENT') {
          baseFilters.push({
            name: 'verificationStatus',
            type: 'dropdown',
            label: 'Status pointage',
            placeholder: 'Status pointage: Tous',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { value: 'en_cours_de_verification', label: 'En cours de pointage' }
            ]
          });
        }

        // Status filter for pointage verifiers: each role filters by their own status (client or comptabilité)
        if (userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') {
          baseFilters.push({
            name: 'verificationStatus',
            type: 'dropdown',
            label: 'Statut',
            placeholder: 'Statut: Tous',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { id: 'OPEN', value: 'ouvert', label: 'Ouvert' },
              { id: 'IN_PROGRESS', value: 'en_cours_de_verification', label: 'En cours' },
              { id: 'VERIFIED', value: 'verifie', label: 'Vérifié' }
            ]
          });
        }

        // Add date filter
        baseFilters.push({
          name: 'dateRange',
          type: 'daterange',
          label: 'Date',
          placeholder: 'Date',
          isMultiSelect: false
        });

        // Add pointage filter only for MANAGER users (not for AGENT or POINTAGE users)
        if (userRole === 'MANAGER') {
          baseFilters.push({
            name: 'requiresPointage',
            type: 'dropdown',
            label: 'Pointage',
            placeholder: 'Pointage',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { value: 'true', label: 'Nécessite Pointage' },
              { value: 'client', label: 'Nécessite Pointage Client' },
              { value: 'comptabilite', label: 'Nécessite Pointage Comptabilité' },
              { value: 'false', label: 'Pas de Pointage' }
            ]
          });
        }

        const filterOptions = baseFilters;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0, width: '100%', maxWidth: '100%', overflow: 'visible' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Component */}
      <Filter
        filters={filterOptions}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onReset={onReset}
        showApplyButton={false}
        onExport={userRole === 'AGENT' ? null : onExport}
        exportFormats={(userRole === 'POINTAGE_CLIENT' || userRole === 'POINTAGE_COMPTABILITE') ? ['CSV'] : undefined}
        hasActiveFilters={hasActiveFilters}
        hasSelection={hasSelection}
        hasResults={transactions && transactions.length > 0}
        onDatePickerOpenChange={onDatePickerOpenChange}
      />

    </Box>
  );
};

export default TransactionFilters;
